<script>
import { authState } from "@app/store/authStore.svelte.js";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import {
    eventGroupState,
    resetEventGroupState,
} from "@app/store/eventGroupStore.svelte.js";
import {
    projectState,
    resetProjectState,
    updateProjectMedia,
    addMediaVersion,
} from "@app/store/projectStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { uiState } from "@app/store/uiStore.svelte.js";
import { issueState } from "@app/store/issueStore.svelte.js";
import { resetMarkerState } from "@app/store/markerStore.svelte.js";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import {
    insertHistoryAction,
    resetHistory,
} from "@app/store/historyStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import {
    updateDuration,
    updatePlayingStatus,
} from "@app/store/playerStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { resetPeaksState } from "@app/lib/timeline/peaksState.svelte.js";
import { onMount } from "svelte";
import { fade, fly } from "svelte/transition";
import { cubicOut } from "svelte/easing";

import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { Circle } from "svelte-loading-spinners";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import tcLib from "@cclib/lib/timecode.js";
/* Firebase */
import firebase from "@app/configs/firebase.js";
import { v4 as uuidv4 } from "uuid";
import EventGroup from "@cclib/classes/eventGroup.js";
import LanguageSelect from "@app/components/inputs/LanguageSelect.svelte";
import voiceLib from "@cclib/providers/textToSpeechAll/voices.js";
import frameRates from "@cclib/dict/frameRates.js";
import * as m from "@app/paraglide/messages.js";

const uid = uuidv4();
let loading = $state(false);
let statusMsg = $state(m.new_project_status_scanning_media());
let audioExtractionComplete = false;
let manifestFileExists = false;
let versionRegisteredForImport = false;
let projectDefaults =
    JSON.parse(localStorage.getItem("cc-project-defaults")) || {};
let eventGroupDefaults =
    JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
let projectName = $state(),
    useLocalStorage = projectDefaults.useLocalStorage,
    projectType = $state("default"),
    incode = $state("00:00:00:00"),
    frameRate = $state(projectDefaults.frameRate || "24"),
    dropFrame = $state(projectDefaults.dropFrame || "false"),
    ffmpegRes,
    videoOutputPath,
    audioOutputPath,
    mediaSource = $state(projectDefaults.mediaSource || "Local Storage"),
    files = $state([]),
    mediaUrl = $state(""),
    /* Advanced Options */
    aspectRatio = $state(projectDefaults.aspectRatio || "16/9"),
    eventGroupType = $state(projectDefaults.eventGroupType || "subtitle"),
    displayName = $state(""),
    language = $state(projectDefaults.language || "en-US"),
    rtl = $state(projectDefaults.rtl),
    fontFamily = $state(eventGroupDefaults.fontFamily || "plexMono"),
    fontSize = $state(eventGroupDefaults.fontSize || 16),
    /* Audio Description (AD) Settings */
    gender = "male",
    selectedLanguage = "English - US",
    virtualVoice = voiceLib[selectedLanguage][gender][0],
    showAdvancedOptions = $state(false),
    mediaProgress = $state(m.new_project_media_progress_starting()),
    audioExtractionWorker = environment.electron
        ? new Worker("./build/workers/audioExtraction.js")
        : null,
    proxyRtWorker = environment.electron
        ? new Worker("./build/workers/proxyRt.js")
        : null;
let canCreateAudioDescription = $derived(authState.ad || !environment.online);
let canCreateLiveProject = $derived(
    environment.online &&
        (authState.plan === "pro" || authState.plan === "enterprise"),
);
let isLiveProject = $derived(projectType === "live");
let generatedLiveProjectName = "";

/* Event Group Standards */
let maxCps = $state(eventGroupDefaults.maxCps || 25),
    maxWpm = $state(eventGroupDefaults.maxWpm || 250),
    maxChars = $state(eventGroupDefaults.maxChars || 32),
    maxLines = $state(eventGroupDefaults.maxLines || 2),
    minDuration = $state(eventGroupDefaults.minDuration ?? 1),
    maxDuration = $state(eventGroupDefaults.maxDuration || 12),
    overlap = $state(eventGroupDefaults.overlap ?? true),
    illegalChars = $state(eventGroupDefaults.illegalChars ?? false);

/* Wizard State */
let currentStep = $state(0);
let stepDirection = $state(1);
let isDragging = $state(false);
let skippedEventGroupStep = $state(false);
let fileInputEl = $state();

let steps = $derived(
    isLiveProject
        ? ["project", "confirm"]
        : ["project", "media", "eventGroup", "confirm"],
);
let currentStepId = $derived(steps[currentStep] ?? "project");
let isFirstStep = $derived(currentStep === 0);
let isLastStep = $derived(currentStep === steps.length - 1);

let fileAccept = $derived(
    mediaSource === "Proxy RT"
        ? ".mxf, .ixf, .aaf, .imf, .gxf, .mov, .mp4, .mpeg, .mpeg2, .mpg, .avi, .mkv, .wav, .mp3, .flac"
        : "video/*,audio/*",
);
let usesFileSource = $derived(
    mediaSource === "Local Storage" || mediaSource === "Proxy RT",
);
let hasMediaSelected = $derived(
    usesFileSource
        ? files.length > 0
        : mediaUrl.trim().length > 0,
);
let projectStepValid = $derived(
    !(projectType === "team" && !(authState.team && authState.team.id)) &&
        !(isLiveProject && !canCreateLiveProject),
);
let canProceed = $derived(
    currentStepId === "project" ? projectStepValid : true,
);

let projectTypeLabel = $derived(
    projectType === "team"
        ? m.new_project_type_team()
        : projectType === "live"
          ? m.new_project_type_live()
          : m.new_project_type_default(),
);
let groupTypeLabel = $derived(
    eventGroupType === "audio description"
        ? m.new_project_group_type_audio_description()
        : eventGroupType === "transcription"
          ? m.new_project_group_type_transcription()
          : m.new_project_group_type_subtitle(),
);
let mediaLocationDisplay = $derived(
    usesFileSource
        ? files[0]?.name || m.new_project_summary_none()
        : mediaUrl || m.new_project_summary_none(),
);
let stepTitles = $derived({
    project: m.new_project_step_project(),
    media: m.new_project_step_media(),
    eventGroup: m.new_project_step_event_group(),
    confirm: m.new_project_step_confirm(),
});

onMount(() => {
    player.addEventListener("hls-manifest-loaded", hlsManifestLoaded);

    return () => {
        if (audioExtractionWorker) {
            audioExtractionWorker.terminate();
        }

        if (proxyRtWorker) {
            proxyRtWorker.terminate();
        }

        player.removeEventListener("hls-manifest-loaded", hlsManifestLoaded);
    };
});

function hlsManifestLoaded() {
    console.log("HLS Manifest Loaded");
    manifestFileExists = true;
    closeModal();
}

async function getFileSize() {
    try {
        const response = await fetch(projectState.media.path, {
            method: "HEAD",
        });
        const fileSize = response.headers.get("content-length");
        return fileSize;
    } catch (err) {
        return false;
    }
}

function validateTc(e) {
    incode = tcLib.tcValidate(
        e.target.value,
        parseFloat(frameRate),
        dropFrame === "false" ? false : true,
    );
}

async function getFileMetadata(filePath, ffmpegFullPath) {
    try {
        const exec = window.exec;
        return await exec(
            `"${ffmpegFullPath.replace("app.asar", "app.asar.unpacked")}" -y -i "${filePath}"`,
        );
    } catch (err) {
        return err.message;
    }
}

function getMediaIncode(ffmpegOutput) {
    let matches = ffmpegOutput.match(
        /(?<=timecode\s+:\s+)\d\d:\d\d:\d\d(:|;)\d\d/,
    );
    let mediaIncode = matches ? matches[0].replace(";", ":") : false;

    console.log("Media incode detected: " + mediaIncode);
    return mediaIncode == "null" ? false : mediaIncode;
}

function getMediaFrameRate(ffmpegOutput) {
    // console.log("GETTING MEDIA FRAME RATE");
    //console.log("FFMPEG OUTPUT:");
    //console.log(ffmpegOutput);

    let matches = ffmpegOutput.match(/\d+\.\d+(?= fps)|\d+(?= fps)/);
    let mediaFrameRate = matches ? matches[0] : false;

    console.log("Media frame rate detected: " + mediaFrameRate);
    let fr = frameRates.frameRateMapping[mediaFrameRate];
    return fr;
}

function getMediaDuration(ffmpegOutput) {
    /*  console.log("GETTING MEDIA DURATION");
        console.log("FFMPEG OUTPUT:");
        console.log(ffmpegOutput); */
    let matches = ffmpegOutput.match(/(?<=Duration:\s)\d\d:\d\d:\d\d\.\d\d/);
    let mediaDuration = matches ? matches[0] : false;

    console.log(mediaDuration);
    if (mediaDuration) {
        mediaDuration = tcLib.tcMsToSec(mediaDuration);
        console.log("media duration in seconds: " + mediaDuration);
    }

    return mediaDuration;
}

function getDefaultLiveProjectName() {
    generatedLiveProjectName = `Live ${new Date().toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    })}`;
    return generatedLiveProjectName;
}

function isGeneratedLiveProjectName(name) {
    return name === generatedLiveProjectName || /^Live .*\d{4}.*$/.test(name || "");
}

function applyLiveEventGroupDefaults() {
    eventGroupType = "subtitle";
    maxCps = 20;
    maxWpm = 300;
    maxChars = 32;
    maxLines = 1;
    minDuration = 0.1;
    maxDuration = 9999;
    overlap = false;
    illegalChars = false;
}

function clampCurrentStep() {
    if (currentStep >= steps.length) {
        currentStep = steps.length - 1;
    }
}

function handleProjectTypeChange(event) {
    const selectedProjectType = event.currentTarget.value;

    if (selectedProjectType === "live") {
        applyLiveEventGroupDefaults();
        if (
            !projectName ||
            isGeneratedLiveProjectName(projectName)
        ) {
            projectName = getDefaultLiveProjectName();
        }
    }

    clampCurrentStep();
}

function goNext() {
    if (!canProceed || loading) {
        return;
    }
    if (currentStep < steps.length - 1) {
        stepDirection = 1;
        skippedEventGroupStep = false;
        currentStep += 1;
    }
}

function goBack() {
    if (loading) {
        return;
    }
    if (currentStep > 0) {
        stepDirection = -1;
        if (steps[currentStep] === "confirm" && skippedEventGroupStep) {
            skippedEventGroupStep = false;
            currentStep = steps.indexOf("media");
        } else {
            currentStep -= 1;
        }
    }
}

function skipToConfirm() {
    if (loading || currentStepId !== "media") {
        return;
    }
    stepDirection = 1;
    skippedEventGroupStep = true;
    currentStep = steps.length - 1;
}

function handleSubmit(event) {
    event.preventDefault();
    if (isLastStep) {
        createProject();
    } else {
        goNext();
    }
}

function resolveProjectName() {
    const trimmed = projectName?.trim();
    if (trimmed) {
        return trimmed;
    }
    if (files.length > 0) {
        return files[0].name.replace(/\.[^/.]+$/, "");
    }
    return "Untitled";
}

function applySelectedFiles(selected) {
    files = selected;
    if (files.length > 0 && !projectName?.trim()) {
        projectName = files[0].name.replace(/\.[^/.]+$/, "");
    }
}

function handleDragOver(event) {
    event.preventDefault();
    isDragging = true;
}

function handleDragLeave(event) {
    event.preventDefault();
    isDragging = false;
}

function handleDrop(event) {
    event.preventDefault();
    isDragging = false;
    applySelectedFiles(Array.from(event.dataTransfer?.files || []));
}

function persistProjectCreation() {
    localStorage.setItem(
        "cc-project-defaults",
        JSON.stringify({
            eventGroupType: eventGroupType,
            projectType: projectType,
            incode: incode,
            frameRate: frameRate.toString(),
            dropFrame: dropFrame === "true" ? "true" : "false",
            mediaSource: mediaSource,
            aspectRatio: aspectRatio,
            useLocalStorage: useLocalStorage,
        }),
    );

    localStorage.setItem(
        "cc-event-group-defaults",
        JSON.stringify({
            ...eventGroupDefaults,
            eventGroupType: eventGroupType,
            name: displayName,
            language: language,
            rtl: rtl,
            fontFamily: fontFamily,
            fontSize: fontSize || 16,
            overlap: overlap,
            illegalChars: illegalChars,
            maxCps: maxCps || 25,
            maxWpm: maxWpm || 250,
            maxChars: maxChars || 32,
            maxLines: maxLines || 2,
            minDuration: minDuration >= 0 ? minDuration : 0.5,
            maxDuration: maxDuration || 12,
        }),
    );

    localStorage.setItem(
        "cc-ad-defaults-new",
        JSON.stringify({
            voice: virtualVoice,
            gender: gender,
            language: selectedLanguage,
        }),
    );

    resetHistory();
    insertHistoryAction({
        name: "create project", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
}

async function createProject() {
    loading = true;
    if (isLiveProject) {
        applyLiveEventGroupDefaults();
    }
    const resolvedProjectName = resolveProjectName();
    player.src = '';
    //User Sweetaler to confirm if user wants to create project
    if (
        (eventGroupState.length > 0 && eventGroupState[0].events.length > 1) ||
        eventGroupState.length > 1
    ) {
        let swalRes = await Swal.fire({
            ...SWAL_BASE,
            titleText: m.new_project_confirm_title(),
            text: m.new_project_confirm_text(),
            showDenyButton: true,
            showCancelButton: false,
            confirmButtonText: m.new_project_confirm_yes(),
            denyButtonText: m.new_project_confirm_no(),
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: SWAL_CLASSES.precheck,
        });

        if (swalRes.isDenied) {
            loading = false;
            hideModal();
            return;
        }
    }

    updateDuration(false);
    uiState.timeline = false;
    projectState.selected = false;

    /* Reset State */
    resetEventGroupState();
    speakerState.length = 0;
    issueState.length = 0;
    resetMarkerState();
    resetPeaksState();
    metadataState.length = 0;

    if (dropFrame && (frameRate !== "29.97" && frameRate !== "59.94")) {
        dropFrame = "false";
    }

    resetProjectState({
        type: projectType,
        name: resolvedProjectName,
        incode: tcLib.tcToSec(
            incode,
            parseFloat(frameRate),
            dropFrame === "false" ? false : true,
        ),
        frameRate: parseFloat(frameRate),
        dropFrame: dropFrame === "false" ? false : true,
        username: environment.online
            ? firebase.auth().currentUser.email
            : "Unknown",
        ownerId: environment.online
            ? firebase.auth().currentUser.uid
            : "Unknown",
        useLocalStorage: useLocalStorage,
        localPath: null,
        folderId : false
    });

    console.log(
        "Project FrameRate and drop Frame: " +
            projectState.frameRate +
            " " +
            projectState.dropFrame,
    );

    // Use a new event group and push it instead of direct assignment
    const newEventGroup = {
        ...new EventGroup({
            type: eventGroupType,
            name: displayName,
            language: language,
            rtl: rtl,
            fontFamily: fontFamily,
            fontSize: fontSize || 16,
            overlap: overlap,
            maxCps: maxCps || 25,
            maxWpm: maxWpm || 250,
            maxChars: maxChars || 32,
            maxLines: maxLines || 2,
            minDuration: minDuration >= 0 ? minDuration : 0.5,
            maxDuration: maxDuration || 12,
            illegalChars: illegalChars ?? false,
            ad: {
                voice: virtualVoice,
                gender: gender,
                language: selectedLanguage,
            },
        }),
    };

    eventGroupState.push(newEventGroup);

    projectState.selected = 0;

    const initialAlignment =
        eventGroupType === "audio description" ||
        eventGroupType === "transcription"
            ? "left"
            : "center";

    eventGroupState[projectState.selected] = insertEvent(
        eventGroupState[projectState.selected],
        {
            start: 2,
            end: 4,
            alignment: initialAlignment,
        },
    );

    if (isLiveProject) {
        manifestFileExists = true;
        audioExtractionComplete = true;
        mediaProgress = m.new_project_media_progress_completed();
        statusMsg = m.new_project_media_import_success();
        persistProjectCreation();
        uiState.timeline = true;
        uiState.quickTools.liveCaptioning = true;
        uiState.quickTools.selected = "liveCaptioning";
        loading = false;
        hideModal();
        return;
    }

    if (!hasMediaSelected) {
        persistProjectCreation();
        uiState.timeline = true;
        loading = false;
        hideModal();
        return;
    }

    versionRegisteredForImport = false;

    updateProjectMedia({
        projectName: resolvedProjectName,
        storage: mediaSource,
        mediaUrl: mediaUrl,
        files: files,
        incode: tcLib.tcToSec(
            incode,
            parseFloat(frameRate),
            dropFrame === "false" ? false : true,
        ),
        frameRate: parseFloat(frameRate),
        dropFrame: dropFrame === "false" ? false : true,
        aspectRatio: aspectRatio,
    });

    if (environment.electron && files && files.length > 0) {
        projectState.media.localPath = await window.webUtils.getPathForFile(files[0]);
    }

    let mediaType = "video/mp4";

    switch (mediaSource) {
        case "Vimeo":
            player.src = projectState.media.path;
            manifestFileExists = true;
            audioExtractionComplete = true;

            break;
        case "YouTube":
            player.src = projectState.media.path;
            manifestFileExists = true;
            audioExtractionComplete = true;

            break;
        case "HLS Manifest":
            audioExtractionComplete = true;
            player.src = projectState.media.path;

            break;
        case "Proxy RT":
            videoOutputPath = os.tmpdir() + path.sep + uid + ".mp4";
            audioOutputPath = os.tmpdir() + path.sep + uid + ".json";

            projectState.media.path = videoOutputPath;

            ffmpegRes = await getFileMetadata(
                projectState.media.localPath,
                window.ffmpegPath
            );

            projectState.media.info = {
                incode: getMediaIncode(ffmpegRes),
                frameRate: getMediaFrameRate(ffmpegRes),
                duration: getMediaDuration(ffmpegRes),
            };

            /* -------------auto detect frame rate start------------- */
            if (
                projectState.media.info.frameRate &&
                projectState.media.info.frameRate != projectState.frameRate
            ) {
                if (
                    await alertUserBoolean(
                        m.new_project_update_frame_rate_prompt({ frameRate: projectState.media.info.frameRate }),
                    )
                ) {
                    projectState.dropFrame =
                        projectState.media.info.frameRate === 29.97 ||
                        projectState.media.info.frameRate === 59.94
                            ? true
                            : false;
                    projectState.frameRate = projectState.media.info.frameRate;
                }
            }
            /* -------------auto detect frame rate end------------- */

            /* -------------auto detect incode start------------- */
            if (
                projectState.media.info.incode &&
                projectState.media.info.incode != incode
            ) {
                if (
                    await alertUserBoolean(
                        m.new_project_update_incode_prompt({ incode: projectState.media.info.incode }),
                    )
                ) {
                    let multiplier = projectState.frameRate === 23.976 || (projectState.frameRate === 29.97 && !projectState.dropFrame) ? 24/23.976 : 1;

                    incode = projectState.media.info.incode;
                    projectState.incode = parseFloat(((tcLib.tcToSec(incode, projectState.frameRate, projectState.dropFrame, true))*multiplier).toFixed(3));

                    console.log("INCODE INFORMATION")
                    console.log(incode, projectState.incode, multiplier);
                }
            }
            /* -------------auto detect incode end------------- */
            audioExtractionWorker.postMessage({
                inputPath: projectState.media.localPath,
                outputPath: audioOutputPath,
                ffmpegPath: window.ffmpegPath,
                duration: projectState.media.info.duration,
            });

            audioExtractionWorker.onmessage = (msg) => {
                //console.log(msg);
                if (msg.data.status === "in_progress") {
                    statusMsg = msg.data.result;
                } else {
                    projectState.media.peaksPath = msg.data.error
                        ? ""
                        : audioOutputPath;
                    projectState.media.useFallback = false;
                    toast.push(
                        `${msg.data.error ? msg.data.error : m.new_project_audio_extraction_success()}`,
                        {
                            classes: [
                                `toast-${msg.data.error ? "danger" : "success"}`,
                            ],
                        },
                    );

                    audioExtractionComplete = true;
                    closeModal();
                }
            };

            proxyRtWorker.postMessage({
                inputPath: projectState.media.localPath,
                outputPath: videoOutputPath,
                ffmpegPath: window.ffmpegPath,
                tmpDir: os.tmpdir(),
                pathSep: path.sep,
            });

            proxyRtWorker.onmessage = async (msg) => {
                //console.log(msg);
                if (msg.data.status === "in_progress") {
                    statusMsg = msg.data.status_msg;
                    mediaProgress = m.new_project_media_progress_in_progress();
                } else if (
                    msg.data.status === "done" &&
                    msg.data.result === "passed"
                ) {
                    manifestFileExists = true;
                    statusMsg = msg.data.status_msg;
                    mediaProgress = m.new_project_media_progress_completed();
                    player.src = videoOutputPath;
                    closeModal();
                } else if (
                    msg.data.status === "done" &&
                    msg.data.result === "error"
                ) {
                    manifestFileExists = true;
                    toast.push(`${msg.data.error}`, {
                        classes: ["toast-danger"],
                    });

                    closeModal();
                }
            };

            break;
        case "Cloud Storage":
            /* Cloud Storage*/
            if (projectState.media.type === "video/quicktime") {
                mediaType = "video/mp4";
            } else if (projectState.media.type === "video/x-matroska") {
                mediaType = "video/mkv";
            } else {
                mediaType = projectState.media.type;
            }

            //Get file size of url
            let fileSize = await getFileSize(projectState.media.path);
            if (fileSize) {
                const sizeInBytes = parseInt(fileSize);
                const sizeInKilobytes = sizeInBytes / 1024;
                const sizeInMegabytes = sizeInKilobytes / 1024;
                if (sizeInMegabytes < 512) {
                    projectState.media.useFallback = false;
                }
            }

            player.src = { src: projectState.media.path, type: mediaType };

            /* Update Flags */
            manifestFileExists = true;
            audioExtractionComplete = true;

            break;
        default:
            /* Local Storage */
            manifestFileExists = true;
            mediaType =
                projectState.media.type === "video/quicktime"
                    ? "video/mp4"
                    : projectState.media.type;

            player.src = { src: environment.electron ? projectState.media.localPath : projectState.media.path, type: mediaType };

            if (environment.electron) {
                console.log("FFMPEG PATH: " + window.ffmpegPath);
                audioOutputPath = os.tmpdir() + path.sep + uid + ".json";
                ffmpegRes = await getFileMetadata(
                    projectState.media.localPath,
                    window.ffmpegPath
                );
                console.log("-----FILE INFO-----");
                console.log(ffmpegRes);
                console.log("-------------------");
                projectState.media.info = {
                    incode: getMediaIncode(ffmpegRes),
                    frameRate: getMediaFrameRate(ffmpegRes),
                    duration: getMediaDuration(ffmpegRes),
                };
                /* Auto detect frame rate */
                if (
                    projectState.media.info.frameRate &&
                    projectState.media.info.frameRate != projectState.frameRate
                ) {
                    if (
                        await alertUserBoolean(
                            m.new_project_update_frame_rate_prompt({ frameRate: projectState.media.info.frameRate }),
                        )
                    ) {
                        projectState.frameRate =
                            projectState.media.info.frameRate;
                        projectState.dropFrame =
                            projectState.media.info.frameRate === 29.97 ||
                            projectState.media.info.frameRate === 59.94
                                ? true
                                : false;
                    }
                }
                /* -------------auto detect frame rate------------- */

                /* Auto detect incode */
                if (
                    projectState.media.info.incode &&
                    projectState.media.info.incode != incode
                ) {
                    if (
                        await alertUserBoolean(
                            m.new_project_update_incode_prompt({ incode: projectState.media.info.incode }),
                        )
                    ) {
                        let multiplier = projectState.frameRate === 23.976 || (projectState.frameRate === 29.97 && !projectState.dropFrame) ? 24/23.976 : 1;

                        incode = projectState.media.info.incode;
                        projectState.incode = parseFloat(((tcLib.tcToSec(incode, projectState.frameRate, projectState.dropFrame, true))*multiplier).toFixed(3));

                        console.log("INCODE INFORMATION")
                        console.log(incode, projectState.incode, multiplier);
                    }
                }
                /* -------------auto detect incode------------- */
                audioExtractionWorker.postMessage({
                    inputPath: projectState.media.localPath,
                    outputPath: audioOutputPath,
                    ffmpegPath: window.ffmpegPath,
                    duration: projectState.media.info.duration,
                });

                audioExtractionWorker.onmessage = (msg) => {
                    //console.log(msg);
                    if (msg.data.status === "in_progress") {
                        statusMsg = msg.data.result;
                    }else if (msg.data.error){
                        projectState.media.peaksData = [[0],[0]];
                        audioExtractionComplete = true;
                        closeModal();
                    } else {
                        projectState.media.peaksPath = msg.data.error
                            ? ""
                            : audioOutputPath;
                        projectState.media.useFallback = false;
                        toast.push(
                            `${(m.new_project_media_import_success())}`,
                            {
                                classes: [
                                    `toast-${msg.data.error ? "danger" : "success"}`,
                                ],
                            },
                        );

                        audioExtractionComplete = true;
                        closeModal();
                    }
                };
            } else {
                /* Running in browser */
                if (files && files.length > 0 && files[0].size < 524288000) {
                    // 500MB (524288000 bytes)
                    projectState.media.useFallback = false;
                }

                audioExtractionComplete = true;
            }
    }

    persistProjectCreation();

    closeModal();
}

function toggleAdvancedOptions() {
    showAdvancedOptions = !showAdvancedOptions;
}

function getMediaIdentity(mediaObj) {
    return mediaObj?.localPath || mediaObj?.path || mediaObj?.name || "";
}

function registerMediaVersionIfNeeded() {
    if (
        environment.electron &&
        getMediaIdentity(projectState.media) &&
        !versionRegisteredForImport
    ) {
        addMediaVersion(projectState.media, projectState.media.name);
        versionRegisteredForImport = true;
    }
}

function closeModal() {
    console.log(
        "Close Modal Called",
        audioExtractionComplete,
        manifestFileExists,
    );
    updatePlayingStatus(false);
    if (
        mediaSource === "Vimeo" ||
        mediaSource === "YouTube" ||
        mediaSource === "Cloud Storage"
    ) {
        console.log("YOUTUBE AND VIMEO AND CLOUD STORAGE");
        setTimeout(() => {
            registerMediaVersionIfNeeded();
            statusMsg = m.new_project_media_import_success();
            uiState.timeline = true;
            hideModal();
        }, 1000);
    }

    if (manifestFileExists && mediaSource === "HLS Manifest") {
        setTimeout(() => {
            registerMediaVersionIfNeeded();
            statusMsg = m.new_project_media_import_success();
            uiState.timeline = true;
            hideModal();
        }, 1000);
    }

    if (
        (audioExtractionComplete || !environment.electron) &&
        mediaSource === "Local Storage"
    ) {
        setTimeout(() => {
            registerMediaVersionIfNeeded();
            statusMsg = m.new_project_media_import_success();
            uiState.timeline = true;
            hideModal();
        }, 1000);
    }

    if (
        audioExtractionComplete &&
        manifestFileExists &&
        mediaSource === "Proxy RT"
    ) {
        setTimeout(() => {
            registerMediaVersionIfNeeded();
            statusMsg = m.new_project_media_import_success();
            uiState.timeline = true;
            hideModal();
        }, 1000);
    }

    return;
}

function alertUserBoolean(msg) {
    let response = Swal.fire({
        ...SWAL_BASE,
        titleText: m.new_project_confirm_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.new_project_confirm_yes(),
        denyButtonText: m.new_project_confirm_no(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.precheck,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else {
            return false;
        }
    });

    return response;
}

// Handler for media source changes
function handleMediaSourceChange() {
    // Clear files array and reset project name when switching away from file-based sources
    if (mediaSource !== "Local Storage" && mediaSource !== "Proxy RT") {
        files = [];

        // Only reset project name if it was derived from a file
        if (projectName && files.length === 0) {
            projectName = '';
        }
    }
}

// Handler for file inputs
function handleFileInput(event) {
    applySelectedFiles(Array.from(event.currentTarget.files || []));
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'newProject' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="newProjectTitle"
    aria-describedby="newProjectDescription"
    aria-modal="true"
    tabindex="-1"
    id="NewProjectModal">
    <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="newProjectTitle">{m.new_project_title()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.new_project_close_aria_label()}
                    onclick={() => hideModal()}></button>
            </div>
            <div class="modal-body">
                <p id="newProjectDescription" class="sr-only small text-muted">
                    {m.new_project_description()}
                </p>
                <form
                    onsubmit={handleSubmit}
                    id="NewProjectForm"
                    aria-label={m.new_project_form_aria_label()}>
                    <ol class="wizard-steps list-unstyled d-flex mb-4">
                        {#each steps as stepId, index (stepId)}
                            <li
                                class="wizard-step flex-fill text-center {index ===
                                currentStep
                                    ? 'active'
                                    : ''} {index < currentStep
                                    ? 'complete'
                                    : ''}">
                                <span class="wizard-step-index">{index + 1}</span>
                                <span
                                    class="wizard-step-title small d-none d-sm-block"
                                    >{stepTitles[stepId]}</span>
                            </li>
                        {/each}
                    </ol>

                    <div class="wizard-viewport">
                        {#key currentStepId}
                            <div
                                class="wizard-panel"
                                in:fly={{
                                    x: stepDirection * 40,
                                    duration: 220,
                                    easing: cubicOut,
                                }}>
                                {#if currentStepId === "project"}
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="projectTypeInput"
                                            id="projectTypeLabel"
                                            >{m.new_project_type_label()}</label>
                                        <select
                                            id="projectTypeInput"
                                            class="form-select"
                                            bind:value={projectType}
                                            onchange={handleProjectTypeChange}
                                            aria-labelledby="projectTypeLabel"
                                            aria-required="true">
                                            <option value="default"
                                                >{m.new_project_type_default()}</option>
                                            {#if authState.team && authState.team.id}
                                                <option value="team"
                                                    >{m.new_project_type_team()}</option>
                                            {/if}
                                            {#if canCreateLiveProject}
                                                <option value="live"
                                                    >{m.new_project_type_live()}</option>
                                            {/if}
                                        </select>
                                    </div>
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="projectNameInput"
                                            id="projectNameLabel"
                                            >{m.new_project_name_label()}</label>
                                        <input
                                            id="projectNameInput"
                                            class="form-control"
                                            type="text"
                                            placeholder={m.new_project_name_placeholder()}
                                            bind:value={projectName}
                                            aria-labelledby="projectNameLabel" />
                                    </div>
                                {:else if currentStepId === "media"}
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="mediaSourceSelect"
                                            id="mediaSourceLabel"
                                            >{m.new_project_media_source_label()}</label>
                                        <select
                                            id="mediaSourceSelect"
                                            class="form-select"
                                            bind:value={mediaSource}
                                            onchange={handleMediaSourceChange}
                                            aria-labelledby="mediaSourceLabel">
                                            <option value="Local Storage"
                                                >{m.new_project_media_source_local_storage()}</option>
                                            {#if environment.online}
                                                <option value="YouTube"
                                                    >{m.new_project_media_source_youtube()}</option>
                                                <option value="Vimeo"
                                                    >{m.new_project_media_source_vimeo()}</option>
                                                <option value="Cloud Storage"
                                                    >{m.new_project_media_source_cloud_storage()}</option>
                                                <option value="HLS Manifest"
                                                    >{m.new_project_media_source_hls_manifest()}</option>
                                            {/if}
                                            {#if environment.electron}
                                                <option value="Proxy RT"
                                                    >{m.new_project_media_source_proxy_rt()}</option>
                                            {/if}
                                        </select>
                                    </div>

                                    <div aria-live="polite" class="mb-3">
                                        {#if usesFileSource}
                                            <p class="mb-2 text-muted small">
                                                {m.new_project_media_location_label()}
                                            </p>
                                            <div
                                                class="drop-zone {isDragging
                                                    ? 'dragging'
                                                    : ''}"
                                                role="button"
                                                tabindex="0"
                                                aria-label={m.new_project_drop_zone_label()}
                                                onclick={() =>
                                                    fileInputEl?.click()}
                                                onkeydown={(e) =>
                                                    (e.key === "Enter" ||
                                                        e.key === " ") &&
                                                    (e.preventDefault(),
                                                    fileInputEl?.click())}
                                                ondragover={handleDragOver}
                                                ondragleave={handleDragLeave}
                                                ondrop={handleDrop}>
                                                {#if files.length > 0}
                                                    <i
                                                        class="bi bi-file-earmark-play drop-zone-icon"
                                                    ></i>
                                                    <p class="mb-1 fw-semibold">
                                                        {files[0].name}
                                                    </p>
                                                    <p
                                                        class="mb-0 small text-muted">
                                                        {m.new_project_drop_zone_change()}
                                                    </p>
                                                {:else}
                                                    <i
                                                        class="bi bi-cloud-arrow-up drop-zone-icon"
                                                    ></i>
                                                    <p class="mb-1 fw-semibold">
                                                        {m.new_project_drop_zone_label()}
                                                    </p>
                                                    <p
                                                        class="mb-0 small text-muted">
                                                        {m.new_project_drop_zone_hint()}
                                                    </p>
                                                {/if}
                                                <input
                                                    bind:this={fileInputEl}
                                                    type="file"
                                                    class="d-none"
                                                    accept={fileAccept}
                                                    onchange={handleFileInput} />
                                            </div>
                                        {:else if mediaSource === "HLS Manifest"}
                                            <label
                                                class="form-label"
                                                for="HlsManifestUrl"
                                                >{m.new_project_hls_manifest_label()}</label>
                                            <input
                                                bind:value={mediaUrl}
                                                type="url"
                                                class="form-control"
                                                id="HlsManifestUrl"
                                                placeholder={m.new_project_hls_manifest_placeholder()} />
                                        {:else if mediaSource === "YouTube"}
                                            <label
                                                class="form-label"
                                                for="YouTubeSourceUrl"
                                                >{m.new_project_youtube_url_label()}</label>
                                            <input
                                                bind:value={mediaUrl}
                                                type="url"
                                                class="form-control"
                                                id="YouTubeSourceUrl"
                                                placeholder={m.new_project_video_url_placeholder()} />
                                        {:else if mediaSource === "Vimeo"}
                                            <label
                                                class="form-label"
                                                for="VimeoSourceUrl"
                                                >{m.new_project_vimeo_url_label()}</label>
                                            <input
                                                bind:value={mediaUrl}
                                                type="url"
                                                class="form-control"
                                                id="VimeoSourceUrl"
                                                placeholder={m.new_project_video_url_placeholder()} />
                                        {:else}
                                            <label
                                                class="form-label"
                                                for="CloudSourceUrl"
                                                >{m.new_project_cloud_url_label()}</label>
                                            <input
                                                bind:value={mediaUrl}
                                                type="url"
                                                class="form-control"
                                                id="CloudSourceUrl"
                                                placeholder={m.new_project_cloud_url_placeholder()} />
                                        {/if}
                                    </div>

                                    <button
                                        type="button"
                                        class="mb-2 dropdown-toggle btn btn-sm btn-warning"
                                        onclick={toggleAdvancedOptions}
                                        aria-expanded={showAdvancedOptions}
                                        aria-controls="advancedOptionsWrapper">
                                        {m.new_project_view_more_options_button()}
                                    </button>

                                    {#if showAdvancedOptions}
                                        <div
                                            id="advancedOptionsWrapper"
                                            class="mt-2"
                                            transition:fade={{ duration: 200 }}
                                            role="region"
                                            aria-label={m.new_project_advanced_options_aria_label()}>
                                            <div class="mb-3">
                                                <label
                                                    for="incodeInput"
                                                    class="form-label"
                                                    >{m.new_project_incode_label()}</label>
                                                <input
                                                    placeholder={m.new_project_incode_placeholder()}
                                                    type="text"
                                                    class="form-control"
                                                    id="incodeInput"
                                                    bind:value={incode}
                                                    onblur={validateTc}
                                                    onkeyup={(e) =>
                                                        e.key === "Enter" &&
                                                        validateTc(e)}
                                                    onfocus={(e) => {
                                                        e.target.select();
                                                    }} />
                                            </div>
                                            <div class="row">
                                                <div class="mb-3 col">
                                                    <label
                                                        class="form-label"
                                                        for="frameRateSelector"
                                                        >{m.new_project_frame_rate_label()}</label>
                                                    <select
                                                        id="frameRateSelector"
                                                        class="form-select"
                                                        bind:value={frameRate}>
                                                        <option value="23.976"
                                                            >23.976</option>
                                                        <option value="24"
                                                            >24</option>
                                                        <option value="25"
                                                            >25</option>
                                                        <option value="29.97"
                                                            >29.97</option>
                                                        <option value="30"
                                                            >30</option>
                                                        <option value="59.94"
                                                            >59.94</option>
                                                        <option value="60"
                                                            >60</option>
                                                    </select>
                                                </div>
                                                {#if ["29.97", "59.94"].indexOf(frameRate) > -1}
                                                    <div class="mb-3 col">
                                                        <label
                                                            class="form-label"
                                                            for="dropFrameSelector"
                                                            >{m.new_project_drop_frame_label()}</label>
                                                        <select
                                                            id="dropFrameSelector"
                                                            class="form-select"
                                                            bind:value={dropFrame}>
                                                            <option value="true"
                                                                >{m.common_true()}</option>
                                                            <option
                                                                value="false"
                                                                >{m.common_false()}</option>
                                                        </select>
                                                    </div>
                                                {/if}
                                            </div>
                                            <div class="mb-3">
                                                <label
                                                    class="form-label"
                                                    for="aspectRatioSelector"
                                                    >{m.new_project_aspect_ratio_label()}</label>
                                                <select
                                                    id="aspectRatioSelector"
                                                    class="form-select"
                                                    bind:value={aspectRatio}>
                                                    <option value="16/9"
                                                        >16:9</option>
                                                    <option value="4/3"
                                                        >4:3</option>
                                                    <option value="1/1"
                                                        >1:1</option>
                                                    <option value="9/16"
                                                        >9:16</option>
                                                    <option value="21/9"
                                                        >21:9</option>
                                                    <option value="143/100"
                                                        >1.43:1</option>
                                                    <option value="185/100"
                                                        >1.85:1</option>
                                                    <option value="235/100"
                                                        >2.35:1</option>
                                                    <option value="239/100"
                                                        >2.39:1</option>
                                                    <option value="276/100"
                                                        >2.76:1</option>
                                                </select>
                                            </div>
                                        </div>
                                    {/if}
                                {:else if currentStepId === "eventGroup"}
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="eventGroupTypeSelect"
                                            >{m.new_project_group_type_label()}</label>
                                        <select
                                            id="eventGroupTypeSelect"
                                            class="form-select"
                                            bind:value={eventGroupType}>
                                            <option value="subtitle"
                                                >{m.new_project_group_type_subtitle()}</option>
                                            {#if canCreateAudioDescription}
                                                <option value="audio description"
                                                    >{m.new_project_group_type_audio_description()}</option>
                                            {/if}
                                            <option value="transcription"
                                                >{m.new_project_group_type_transcription()}</option>
                                        </select>
                                    </div>
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="displayNameInput"
                                            >{m.new_project_display_name_label()}</label>
                                        <input
                                            id="displayNameInput"
                                            class="form-control"
                                            placeholder={m.new_project_display_name_placeholder()}
                                            type="text"
                                            bind:value={displayName} />
                                    </div>
                                    <div class="mb-3">
                                        <label
                                            class="form-label"
                                            for="languageSelect"
                                            >{m.new_project_language_label()}</label>
                                        <LanguageSelect id="languageSelect" bind:value={language} />
                                    </div>
                                    <div class="form-check form-switch mb-2">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            id="rtlSwitch"
                                            bind:checked={rtl} />
                                        <label
                                            class="form-check-label"
                                            for="rtlSwitch"
                                            >{m.new_project_rtl_label()}</label>
                                    </div>
                                    <hr />
                                    <p class="lead text-warning m-0">
                                        {m.new_project_standards_label()}
                                    </p>
                                    <div class="row">
                                        <div class="col-6 mb-3">
                                            <label
                                                class="form-label"
                                                for="maxCpsInput"
                                                >{m.max_characters_per_second()}</label>
                                            <input
                                                id="maxCpsInput"
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="9999"
                                                class="form-control"
                                                bind:value={maxCps} />
                                        </div>
                                        <div class="col-6 mb-3">
                                            <label
                                                class="form-label"
                                                for="maxWpmInput"
                                                >{m.max_words_per_minute()}</label>
                                            <input
                                                id="maxWpmInput"
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="9999"
                                                class="form-control"
                                                bind:value={maxWpm} />
                                        </div>
                                        <div class="col-6 mb-3">
                                            <label
                                                class="form-label"
                                                for="maxCharsInput"
                                                >{m.max_characters_per_line()}</label>
                                            <input
                                                id="maxCharsInput"
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="9999"
                                                class="form-control"
                                                bind:value={maxChars} />
                                        </div>
                                        <div class="col-6 mb-3">
                                            <label
                                                class="form-label"
                                                for="maxLinesInput"
                                                >{m.max_lines_per_event()}</label>
                                            <input
                                                id="maxLinesInput"
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="9999"
                                                class="form-control"
                                                bind:value={maxLines} />
                                        </div>
                                        <div class="col-6 mb-3">
                                            <label
                                                class="form-label"
                                                for="minDurationInput"
                                                >{m.min_event_duration()}</label>
                                            <input
                                                id="minDurationInput"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="9999"
                                                class="form-control"
                                                bind:value={minDuration} />
                                        </div>
                                        <div class="col-6 mb-3">
                                            <label
                                                class="form-label"
                                                for="maxDurationInput"
                                                >{m.max_event_duration()}</label>
                                            <input
                                                id="maxDurationInput"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="9999"
                                                class="form-control"
                                                bind:value={maxDuration} />
                                        </div>
                                        <div class="col-6 mb-3">
                                            <div class="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    class="form-check-input"
                                                    id="overlapCheck"
                                                    bind:checked={overlap} />
                                                <label
                                                    class="form-check-label"
                                                    for="overlapCheck"
                                                    >{m.overlap_detection()}</label>
                                            </div>
                                        </div>
                                        <div class="col-6 mb-3">
                                            <div class="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    class="form-check-input"
                                                    id="illegalCharsCheck"
                                                    bind:checked={illegalChars} />
                                                <label
                                                    class="form-check-label"
                                                    for="illegalCharsCheck"
                                                    >{m.detect_illegal_characters_608()}</label>
                                            </div>
                                        </div>
                                    </div>
                                {:else}
                                    

                                    <div class="summary-cards">
                                        <section class="summary-card">
                                            <header class="summary-card-head">
                                                <i class="bi bi-folder2-open"></i>
                                                <span
                                                    >{m.new_project_summary_project_title()}</span>
                                            </header>
                                            <div class="summary-card-body">
                                                <div class="summary-item">
                                                    <span class="summary-key"
                                                        >{m.new_project_type_label()}</span>
                                                    <span class="summary-val"
                                                        >{projectTypeLabel}</span>
                                                </div>
                                                <div class="summary-item">
                                                    <span class="summary-key"
                                                        >{m.new_project_name_label()}</span>
                                                    <span class="summary-val"
                                                        >{resolveProjectName()}</span>
                                                </div>
                                            </div>
                                        </section>

                                        {#if !isLiveProject}
                                            <section class="summary-card">
                                                <header class="summary-card-head">
                                                    <i class="bi bi-film"></i>
                                                    <span
                                                        >{m.new_project_summary_media_title()}</span>
                                                </header>
                                                <div class="summary-card-body">
                                                    <div class="summary-item">
                                                        <span class="summary-key"
                                                            >{m.new_project_media_source_label()}</span>
                                                        <span class="summary-val"
                                                            >{mediaSource}</span>
                                                    </div>
                                                    <div
                                                        class="summary-item summary-item-wide">
                                                        <span class="summary-key"
                                                            >{m.new_project_media_location_label()}</span>
                                                        <span
                                                            class="summary-val text-truncate"
                                                            title={mediaLocationDisplay}
                                                            >{mediaLocationDisplay}</span>
                                                    </div>
                                                    <div class="summary-item">
                                                        <span class="summary-key"
                                                            >{m.new_project_frame_rate_label()}</span>
                                                        <span class="summary-val"
                                                            >{frameRate}</span>
                                                    </div>
                                                    <div class="summary-item">
                                                        <span class="summary-key"
                                                            >{m.new_project_drop_frame_label()}</span>
                                                        <span class="summary-val"
                                                            >{dropFrame ===
                                                            "true"
                                                                ? m.common_true()
                                                                : m.common_false()}</span>
                                                    </div>
                                                    <div class="summary-item">
                                                        <span class="summary-key"
                                                            >{m.new_project_incode_label()}</span>
                                                        <span class="summary-val"
                                                            >{incode}</span>
                                                    </div>
                                                    <div class="summary-item">
                                                        <span class="summary-key"
                                                            >{m.new_project_aspect_ratio_label()}</span>
                                                        <span class="summary-val"
                                                            >{aspectRatio.replace(
                                                                "/",
                                                                ":",
                                                            )}</span>
                                                    </div>
                                                </div>
                                            </section>
                                        {/if}

                                        <section class="summary-card">
                                            <header class="summary-card-head">
                                                <i class="bi bi-card-list"></i>
                                                <span
                                                    >{m.new_project_summary_event_group_title()}</span>
                                            </header>
                                            <div class="summary-card-body">
                                                <div class="summary-item">
                                                    <span class="summary-key"
                                                        >{m.new_project_group_type_label()}</span>
                                                    <span class="summary-val"
                                                        >{groupTypeLabel}</span>
                                                </div>
                                                <div class="summary-item">
                                                    <span class="summary-key"
                                                        >{m.new_project_display_name_label()}</span>
                                                    <span class="summary-val"
                                                        >{displayName ||
                                                            m.new_project_summary_none()}</span>
                                                </div>
                                                <div class="summary-item">
                                                    <span class="summary-key"
                                                        >{m.new_project_language_label()}</span>
                                                    <span class="summary-val"
                                                        >{language}</span>
                                                </div>
                                                <div class="summary-item">
                                                    <span class="summary-key"
                                                        >{m.new_project_rtl_label()}</span>
                                                    <span class="summary-val"
                                                        >{rtl
                                                            ? m.common_true()
                                                            : m.common_false()}</span>
                                                </div>
                                            </div>
                                            <div class="summary-standards">
                                                <span class="summary-chip">
                                                    <span class="summary-chip-val"
                                                        >{maxCps}</span>
                                                    <span class="summary-chip-key"
                                                        >CPS</span>
                                                </span>
                                                <span class="summary-chip">
                                                    <span class="summary-chip-val"
                                                        >{maxWpm}</span>
                                                    <span class="summary-chip-key"
                                                        >WPM</span>
                                                </span>
                                                <span class="summary-chip">
                                                    <span class="summary-chip-val"
                                                        >{maxChars}</span>
                                                    <span class="summary-chip-key"
                                                        >CPL</span>
                                                </span>
                                                <span class="summary-chip">
                                                    <span class="summary-chip-val"
                                                        >{maxLines}</span>
                                                    <span class="summary-chip-key"
                                                        >{m.max_lines_per_event()}</span>
                                                </span>
                                            </div>
                                        </section>
                                    </div>
                                {/if}
                            </div>
                        {/key}
                    </div>
                </form>
            </div>
            <div class="modal-footer">
                {#if !isFirstStep}
                    <button
                        onclick={goBack}
                        class="btn btn-outline-secondary me-auto"
                        type="button"
                        disabled={loading}>
                        <i class="bi bi-arrow-left"></i>
                        {m.new_project_back_button()}
                    </button>
                {/if}
                {#if loading}
                    <div
                        aria-live="polite"
                        role="status"
                        class="d-flex align-items-center {isFirstStep
                            ? 'me-auto'
                            : ''}">
                        <Circle
                            size="30"
                            color="#1eb4b2"
                            unit="px"
                            duration="1s" />
                        <p class="text-muted small mb-0 ms-2">
                            {statusMsg} | {mediaProgress}
                        </p>
                    </div>
                {/if}
                {#if isLastStep}
                    <button
                        onclick={createProject}
                        class="btn btn-primary ms-2"
                        type="button"
                        disabled={loading ||
                            (projectType === "team" &&
                                !(authState.team && authState.team.id)) ||
                            (isLiveProject && !canCreateLiveProject)}
                        aria-label={m.new_project_create_aria_label()}>
                        {m.new_project_create_button()}
                    </button>
                {:else}
                    {#if currentStepId === "media"}
                        <button
                            onclick={skipToConfirm}
                            class="btn btn-outline-primary ms-2"
                            type="button"
                            disabled={loading}>
                            {m.new_project_skip_to_confirm_button()}
                        </button>
                    {/if}
                    <button
                        onclick={goNext}
                        class="btn btn-primary ms-2"
                        type="button"
                        disabled={loading || !canProceed}>
                        {m.new_project_next_button()}
                        <i class="bi bi-arrow-right"></i>
                    </button>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    .wizard-steps {
        margin: 0;
        padding: 0;
    }

    .wizard-step {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
        color: var(--bs-secondary-color, #6c757d);
    }

    .wizard-step::before {
        content: "";
        position: absolute;
        top: 14px;
        left: calc(-50% + 14px);
        width: calc(100% - 28px);
        height: 2px;
        background: var(--bs-border-color, #dee2e6);
        z-index: 0;
    }

    .wizard-step:first-child::before {
        display: none;
    }

    .wizard-step-index {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--bs-border-color, #dee2e6);
        color: var(--bs-body-color, #212529);
        font-size: 0.85rem;
        font-weight: 600;
    }

    .wizard-step.active {
        color: var(--bs-primary, #0d6efd);
        font-weight: 600;
    }

    .wizard-step.active .wizard-step-index {
        background: var(--bs-primary, #0d6efd);
        color: #fff;
    }

    .wizard-step.complete .wizard-step-index,
    .wizard-step.complete::before {
        background: var(--bs-success, #198754);
        color: #fff;
    }

    .wizard-viewport {
        position: relative;
        overflow: hidden;
    }

    .wizard-panel {
        width: 100%;
    }

    .drop-zone {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        padding: 2rem 1rem;
        border: 2px dashed var(--bs-border-color, #ced4da);
        border-radius: 0.5rem;
        background: var(--bs-tertiary-bg, #f8f9fa);
        text-align: center;
        cursor: pointer;
        transition:
            border-color 0.15s ease-in-out,
            background-color 0.15s ease-in-out;
    }

    .drop-zone:hover,
    .drop-zone:focus-visible {
        border-color: var(--bs-primary, #0d6efd);
        outline: none;
    }

    .drop-zone.dragging {
        border-color: var(--bs-primary, #0d6efd);
        background: var(--bs-primary-bg-subtle, #cfe2ff);
    }

    .drop-zone-icon {
        font-size: 2rem;
        color: var(--bs-primary, #0d6efd);
    }

    .summary-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 1rem;
    }

    .summary-card {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 0.75rem;
        background: var(--bs-body-bg, #fff);
        overflow: hidden;
    }

    .summary-card-head {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.6rem 0.9rem;
        font-weight: 600;
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: var(--bs-secondary-color, #6c757d);
        background: var(--bs-tertiary-bg, #f8f9fa);
        border-bottom: 1px solid var(--bs-border-color, #dee2e6);
    }

    .summary-card-head i {
        color: var(--bs-primary, #0d6efd);
        font-size: 1rem;
    }

    .summary-card-body {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem 1rem;
        padding: 0.9rem;
    }

    .summary-item {
        display: flex;
        flex-direction: column;
        gap: 0.1rem;
        min-width: 0;
    }

    .summary-item-wide {
        grid-column: 1 / -1;
    }

    .summary-key {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: var(--bs-secondary-color, #6c757d);
    }

    .summary-val {
        font-weight: 600;
        word-break: break-word;
    }

    .summary-standards {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        padding: 0 0.9rem 0.9rem;
    }

    .summary-chip {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1 1 auto;
        min-width: 56px;
        padding: 0.4rem 0.6rem;
        border-radius: 0.5rem;
        background: var(--bs-tertiary-bg, #f8f9fa);
        border: 1px solid var(--bs-border-color, #dee2e6);
        line-height: 1.1;
    }

    .summary-chip-val {
        font-weight: 700;
        font-size: 1rem;
        color: var(--bs-primary, #0d6efd);
    }

    .summary-chip-key {
        font-size: 0.65rem;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: var(--bs-secondary-color, #6c757d);
    }
</style>
